import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check, RefreshCw, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

type Alert = {
  id: string;
  kind: 'failure_spike' | 'worker_crash';
  severity: 'info' | 'warning' | 'critical';
  message: string;
  failure_count: number;
  created_at: string;
};

const SEVERITY_TONE: Record<Alert['severity'], string> = {
  info: 'border-primary/40 bg-primary/5 text-foreground',
  warning: 'border-amber-500/50 bg-amber-500/10 text-foreground',
  critical: 'border-destructive/60 bg-destructive/10 text-foreground',
};

/**
 * Surfaces unacknowledged OCR health alerts inside the admin shell so
 * spikes in job failures or worker stalls are visible without leaving
 * whichever admin page you're on. Polls every 60s and can be dismissed
 * per-alert (which writes acknowledged_at on the row).
 */
export default function OcrHealthAlertBanner() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('ocr_health_alerts')
      .select('id, kind, severity, message, failure_count, created_at')
      .is('acknowledged_at', null)
      .order('created_at', { ascending: false })
      .limit(5);
    if (error) return; // silently — admins don't need this noise
    setAlerts((data ?? []) as Alert[]);
  }, []);

  useEffect(() => {
    load();
    const id = window.setInterval(load, 60_000);
    return () => window.clearInterval(id);
  }, [load]);

  const runCheckNow = async () => {
    setLoading(true);
    try {
      await supabase.functions.invoke('ocr-health-monitor', { body: {} });
      await load();
      toast({ title: 'Health check complete', description: 'OCR worker scanned for issues.' });
    } catch (e) {
      toast({
        title: 'Health check failed',
        description: e instanceof Error ? e.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const ackAlert = async (id: string) => {
    const { data: u } = await supabase.auth.getUser();
    const { error } = await supabase
      .from('ocr_health_alerts')
      .update({
        acknowledged_at: new Date().toISOString(),
        acknowledged_by: u?.user?.id ?? null,
      })
      .eq('id', id);
    if (error) {
      toast({ title: 'Could not dismiss alert', description: error.message, variant: 'destructive' });
      return;
    }
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  };

  if (alerts.length === 0) return null;

  return (
    <div className="space-y-2 px-3 py-2">
      {alerts.map((a) => (
        <div
          key={a.id}
          role="alert"
          className={`flex items-start gap-3 rounded-md border p-3 ${SEVERITY_TONE[a.severity]}`}
        >
          <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" aria-hidden />
          <div className="flex-1 min-w-0 text-sm">
            <div className="font-semibold">
              {a.kind === 'worker_crash' ? 'OCR worker stalled' : 'OCR failure spike'}
            </div>
            <div className="text-muted-foreground">{a.message}</div>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-xs">
              <Link to="/admin/ocr-review" className="underline">
                Open OCR review
              </Link>
              <span className="text-muted-foreground">
                fired {new Date(a.created_at).toLocaleString()}
              </span>
            </div>
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <Button
              size="sm"
              variant="ghost"
              onClick={runCheckNow}
              disabled={loading}
              title="Re-run health check now"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => ackAlert(a.id)}
              title="Dismiss alert"
            >
              <Check className="h-4 w-4" />
              <span className="sr-only">Dismiss</span>
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}